@if (generationContext() === 'aps') {
<div class="d-flex flex-column gap-3 h-100">
  <div class="d-flex align-items-center">
    <div class="form-check me-3">
      <input
        class="form-check-input"
        type="checkbox"
        [checked]="allSelected()"
        [indeterminate]="someSelected()"
        [disabled]="!canWriteDeals()"
        (change)="toggleAllDocuments($event)"
      />
    </div>
    <div class="d-flex flex-column">
      <h3 class="m-0">Document Sequence</h3>
      <p class="m-0 text-muted">All selected documents will be combined into one PDF in the following sequence.</p>
    </div>
    @if (canWriteDeals()) {
    <div class="d-flex align-items-center gap-2 mb-2 ms-auto">
      <button
        class="btn btn-primary btn-h42"
        (click)="generateContract()"
        [disabled]="contractDetails().contractDate === null"
        appBtnLoader="generate-contract"
      >
        Generate Contract
      </button>
      <button
        class="btn btn-primary btn-h42"
        (click)="requestUploadExternalContract(); $event.stopPropagation()"
        [disabled]="!isContractReady()"
      >
        Upload External Document
      </button>
    </div>
    }
  </div>

  <div class="bg-light p-3 rounded-4 d-flex flex-column gap-3">
    <div
      class="drop-zone-top"
      pDroppable="document-template"
      (onDrop)="onTemplateDrop(0)"
      [class.drop-zone-disabled]="draggedTemplateId() === null"
    ></div>
    @for (template of apsTemplates(); track template.id; let i = $index) {
    <div
      class="bg-white shadow-sm rounded-4 overflow-hidden p-3 template-card"
      pDraggable="document-template"
      [pDraggableDisabled]="isTemplatePositionLocked(template) || !canWriteDeals()"
      (onDragStart)="onTemplateDragStart(template)"
      (onDragEnd)="onTemplateDragEnd()"
      [class.template-locked]="isTemplatePositionLocked(template)"
      [class.template-draggable]="!isTemplatePositionLocked(template)"
      [class.template-dragging]="draggedTemplateId() === template.id"
    >
      <div class="d-flex align-items-center gap-2">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            id="template-{{ i }}"
            [checked]="template.isSelected"
            [disabled]="!canWriteDeals()"
            (click)="$event.stopPropagation()"
            (change)="toggleDocumentSelection(template.id)"
          />
        </div>
        <div class="d-flex align-items-center gap-2">
          <span class="text-muted fs-18 fw-500">#{{ i + 1 }}.</span>
          <h3 class="m-0 fs-18 fw-500">{{ template.name }}</h3>
          @if (isTemplatePositionLocked(template)) {
          <span class="badge bg-secondary-subtle text-secondary fw-500">Locked</span>
          } @if (recentlyUpdatedTemplateId() === template.id) {
          <span class="badge bg-success-subtle text-success fw-500">Updated</span>
          }
        </div>
        <span class="ms-auto d-flex align-items-center gap-3">
          <i
            class="bi bi-grip-vertical fs-18 text-muted"
            [class.text-secondary]="isTemplatePositionLocked(template)"
            [title]="isTemplatePositionLocked(template) ? 'Position locked' : 'Drag to reorder'"
          ></i>
          <i
            class="bi bi-eye text-primary"
            role="button"
            (click)="openDocumentPreviewModal(template); $event.stopPropagation()"
          ></i>
          @if (template.isCustom && canWriteDeals()) {
          <i
            class="bi bi-trash text-danger"
            role="button"
            (click)="deleteDocument(template.id); $event.stopPropagation()"
          ></i>
          }
        </span>
      </div>
    </div>
    <div class="d-flex flex-column gap-2">
      @if (!dragStarted() && canWriteDeals()) {
      <div class="d-flex align-self-center gap-2">
        <button
          class="btn btn-outline-secondary btn-h35 text-nowrap"
          (click)="fileInput.click(); insertionPosition.set(i + 1)"
        >
          <i class="bi bi-plus-circle fs-14"></i>
          Insert at Position {{ i + 2 }}
        </button>
        <button
          class="btn btn-outline-primary btn-h35 text-nowrap"
          [disabled]="isLegalPlanAttached()"
          (click)="insertLegalPlan(i + 1)"
        >
          <i class="bi bi-plus-circle fs-14"></i>
          Insert Site Plan
        </button>
        <button
          class="btn btn-outline-primary btn-h35"
          [disabled]="isAddendumAttached()"
          (click)="fetchAddendums(template.id)"
        >
          <i class="bi bi-plus-circle fs-14"></i>
          Insert Schedule PE
        </button>
      </div>
      } @if (dragStarted()) {
      <div
        class="drop-zone w-100 text-center"
        pDroppable="document-template"
        (onDrop)="onTemplateDrop(i + 1)"
        [class.drop-zone-disabled]="draggedTemplateId() === null"
      >
        <span class="text-muted fs-14">Drop document here</span>
      </div>
      }
    </div>
    }
  </div>
</div>

<input #fileInput type="file" class="d-none" accept=".pdf,.doc,.docx" (change)="onFileChange($event)" />
} @else {
<div>
  <!-- Section header: title, guidance, and the primary action, matching the other panels. -->
  <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
    <div class="flex-grow-1">
      <h3 class="fs-18 fw-500 m-0 text-dark">COOP Documents</h3>
      <p class="fs-14 mb-0 mt-1 text-muted">Generate a COOP document for this deal.</p>
    </div>

    <!-- With one pending template the action belongs in the header. With several there
         is no single subject for it, so each row carries its own Generate instead. -->
    @if (canWriteDeals() && miscTemplates().length === 1) {
    <button
      type="button"
      class="btn btn-h35 btn-primary flex-shrink-0 text-nowrap"
      (click)="generateMiscDocument(miscTemplates()[0]); $event.stopPropagation()"
      [appBtnLoader]="generateDocumentButtonId(miscTemplates()[0])"
    >
      <i class="bi bi-file-earmark-check lh-1"></i>
      Generate
    </button>
    }
  </div>

  <div class="d-flex flex-column gap-2">
    @for (template of miscTemplates(); track template.id) {
    <!-- Each template is content, so anything acting on it sits on its own card. -->
    <div class="align-items-center bg-white border d-flex gap-3 p-3 rounded-3">
      <span
        class="align-items-center bg-body-secondary d-flex flex-shrink-0 justify-content-center rounded-3 text-dark wh-40"
      >
        <i class="bi bi-file-earmark-text"></i>
      </span>

      <span class="flex-fill overflow-hidden">
        <span class="d-block fw-500 text-dark text-truncate" [title]="template.name">{{ template.name }}</span>
      </span>

      <span class="align-items-center d-flex flex-shrink-0 gap-2">
        <button
          type="button"
          class="align-items-center border-0 d-inline-flex document-icon-btn justify-content-center p-0 rounded-3 wh-32"
          title="View document"
          aria-label="View document"
          (click)="openDocumentPreviewModal(template); $event.stopPropagation()"
        >
          <i class="bi bi-eye"></i>
        </button>
        @if (canWriteDeals() && miscTemplates().length > 1) {
        <button
          type="button"
          class="btn btn-h35 btn-primary text-nowrap"
          (click)="generateMiscDocument(template); $event.stopPropagation()"
          [appBtnLoader]="generateDocumentButtonId(template)"
        >
          <i class="bi bi-file-earmark-check lh-1"></i>
          Generate
        </button>
        }
      </span>
    </div>
    }
  </div>
</div>
}

<ng-template #documentPreviewModal let-modal>
  <div class="d-flex flex-column h-100">
    <div class="modal-header pt-3 pb-0">
      <h2 class="modal-title">{{ selectedDocument()?.name }}</h2>
      <span (click)="modal.dismiss('Cross click')" role="button" class="ms-auto">
        <svg-icon [name]="'modal-close-menu'" class="svg-32"></svg-icon>
      </span>
    </div>
    <div class="modal-body overflow-y-auto">
      @if (selectedDocument()?.url) {
      <lib-doc-preview [article]="selectedDocument()!.url"></lib-doc-preview>
      } @else {
      <div class="d-flex justify-content-center align-items-center h-100">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      }
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-shadow" (click)="modal.dismiss('Close')">Close</button>
    </div>
  </div>
</ng-template>
